import Security from '@mui/icons-material/Security';
import { Box, Divider, Stack } from '@mui/material';
import { getRequestEvaluations } from 'lib/api/decisionEngine';
import {
  ContentCard,
  ErrorState,
  IconTitleCard,
  IconTitleCardSkeleton,
  MobileBody2,
  StatusChip,
} from 'lib/mobileDesignSystem';
import type { Evaluation } from 'lib/types/models';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getEvaluationStatusColor } from '../utils/evaluationStatus';

const RISK_DESCRIPTION = 'Evaluación de Riesgo';
const RISK_SKELETON_COUNT = 2;
const LABEL_EVALUACIONES = 'Evaluaciones';
const LABEL_ULTIMO_EVALUADOR = 'Último evaluador';

export type RiskTabProduct = 'factoring' | 'confirming' | 'creditLine';

function getEvaluationNodesPath(
  product: RiskTabProduct,
  requestId: string,
  evaluationId: number,
): string {
  const base =
    product === 'factoring'
      ? '/finance/factoring'
      : product === 'confirming'
        ? '/finance/confirming'
        : '/finance/credit-line';
  return `${base}/${requestId}/evaluation/${evaluationId}/nodes`;
}

export interface SharedRiskTabProps {
  product: RiskTabProduct;
  requestId: string | null;
  quoteId?: number | null;
}

const SharedRiskTab: React.FC<SharedRiskTabProps> = ({
  product,
  requestId,
  quoteId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const requestIdNum =
    requestId != null && requestId !== '' ? Number(requestId) : null;

  const isFactoring = product === 'factoring';
  const query1 = getRequestEvaluations(dispatch, {
    requestType: isFactoring
      ? 'factoring-request'
      : product === 'confirming'
        ? 'confirming-request'
        : 'creditLine',
    requestId: isFactoring ? (quoteId ?? null) : requestIdNum,
  });
  const query2 = isFactoring
    ? getRequestEvaluations(dispatch, {
        requestType: 'factoring-request-payers',
        requestId: requestIdNum,
      })
    : null;

  const evaluationLists = useMemo(() => {
    const list1 = Object.values(query1.data ?? {});
    if (query2) {
      const list2 = Object.values(query2.data ?? {});
      return [...list1, ...list2];
    }
    return list1;
  }, [query1.data, query2?.data]);

  const isPending = query2
    ? query1.isPending || query2.isPending
    : query1.isPending;
  const hasEvaluations = evaluationLists.some((list) => list.length > 0);

  const getNavigateState = (evaluation: Evaluation) =>
    isFactoring ? { evaluation, quoteId } : { evaluation };

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      {isPending ? (
        <>
          {Array.from({ length: RISK_SKELETON_COUNT }, (_, i) => (
            <ContentCard key={`skeleton-${i}`}>
              <Stack spacing={0}>
                <IconTitleCardSkeleton />
              </Stack>
            </ContentCard>
          ))}
        </>
      ) : !hasEvaluations ? (
        <ErrorState message="No hay evaluaciones de riesgo para esta operación." />
      ) : (
        <Stack spacing={2}>
          {evaluationLists.map((riskData: Evaluation[], index: number) => {
            if (riskData.length === 0) return null;
            const first = riskData[0];
            return (
              <Box
                key={`${first.id}-${index}`}
                component="button"
                type="button"
                onClick={() =>
                  navigate(
                    getEvaluationNodesPath(product, requestId ?? '', first.id),
                    { state: getNavigateState(first) },
                  )
                }
                sx={{
                  cursor: 'pointer',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                }}
              >
                <ContentCard>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <StatusChip
                        text={first.statusNode.name}
                        type={getEvaluationStatusColor(first.statusNode.name)}
                      />
                    </Box>
                    <Box>
                      <IconTitleCard
                        icon={<Security fontSize="small" />}
                        title={first.label}
                        description={RISK_DESCRIPTION}
                        sx={{ px: 1.5, py: 1.25 }}
                      />
                    </Box>
                    <Divider orientation="horizontal" flexItem sx={{ my: 1 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 7,
                      }}
                    >
                      <Stack direction="column" spacing={0.25}>
                        <MobileBody2
                          component="span"
                          sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                        >
                          {LABEL_ULTIMO_EVALUADOR}:
                        </MobileBody2>
                        <MobileBody2 component="span">
                          {first.reviewerName}
                        </MobileBody2>
                      </Stack>
                      <Stack direction="column" spacing={0.25}>
                        <MobileBody2
                          component="span"
                          sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                        >
                          {LABEL_EVALUACIONES}:
                        </MobileBody2>
                        <MobileBody2 component="span">
                          {riskData.length}
                        </MobileBody2>
                      </Stack>
                    </Box>
                  </Stack>
                </ContentCard>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default SharedRiskTab;
